<template>
    <v-list nav dense>
        <v-subheader class="pl-0">
            <v-hover v-slot:default="{ hover }">
                <v-btn small text color="grey darken-1" class="text-caption" @click="$emit('update:expanded', !expanded)">
                    ORGANIZATION SETTINGS
                    <v-icon v-if="hover" right small>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
            </v-hover>
        </v-subheader>
        <v-expand-transition>
            <div v-show="expanded">
                <OrganizationMenu :collapsed="collapsed" />
                <v-list-item
                    :class="{ 'ml-2 drawer-active-item': !collapsed }"
                    active-class="primary--text"
                    :to="{ name: 'config-org-general', params: { oid: currentOrgOid } }">
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-cog-outline</v-icon>
                            </template>
                            <span>General</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>General</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <v-list-item
                    v-if="isOrgManager"
                    :class="{ 'ml-2 drawer-active-item': !collapsed }"
                    active-class="primary--text"
                    :to="{ name: 'config-org-users', params: { oid: currentOrgOid } }">
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-account-group-outline</v-icon>
                            </template>
                            <span>Users</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Users</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action v-if="!collapsed" class="my-0">
                        <OrganizationInvite ref="drawerOrganizationInvite">
                            <template v-slot:activator>
                                <v-btn text small class="px-2" min-width="0" @click.stop.prevent="openOrgInviteFromDrawer">
                                    <v-icon small>mdi-plus</v-icon>
                                </v-btn>
                            </template>
                        </OrganizationInvite>
                    </v-list-item-action>
                </v-list-item>

                <!-- <v-list-item
                    :class="{ 'ml-2 drawer-active-item': !collapsed }"
                    active-class="primary--text"
                    :to="{ name: 'config-org-integrations', params: { oid: currentOrgOid } }">
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-puzzle-outline</v-icon>
                            </template>
                            <span>Integrations</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Integrations</v-list-item-title>
                    </v-list-item-content>
                </v-list-item> -->

                <v-list-item
                    :class="{ 'ml-2 drawer-active-item': !collapsed }"
                    active-class="primary--text"
                    :to="{ name: 'config-org-secrets', params: { oid: currentOrgOid } }">
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-lock-outline</v-icon>
                            </template>
                            <span>Secrets</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Secrets</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action v-if="isOrgManager && !collapsed" class="my-0">
                        <SecretCreateDialog :level="3" :entity-id="currentOrgOid">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                    <v-icon small>mdi-plus</v-icon>
                                </v-btn>
                            </template>
                        </SecretCreateDialog>
                    </v-list-item-action>
                </v-list-item>

                <v-list-item
                    :class="{ 'ml-2 drawer-active-item': !collapsed }"
                    active-class="primary--text"
                    :to="{ name: 'config-org-tokens', params: { oid: currentOrgOid } }">
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-key-outline</v-icon>
                            </template>
                            <span>Tokens</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Tokens</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action v-if="isOrgManager && !collapsed" class="my-0">
                        <div @click.stop.prevent>
                            <CreateAndEditTokenDialog
                                is-icon-dialog
                                activator-icon="mdi-plus"
                                icon-activator-style="text"
                                :show-tooltip="false"
                                :disabled="!currentOrgOid" />
                        </div>
                    </v-list-item-action>
                </v-list-item>
            </div>
        </v-expand-transition>
    </v-list>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

const OrganizationMenu = () => import('@/components/OrganizationMenu.vue')
const SecretCreateDialog = () => import('@/components/SecretCreateDialog.vue')
const CreateAndEditTokenDialog = () => import('@/modules/organization/components/TheOrgCreateAndEditTokenDialog.vue')
const OrganizationInvite = () => import('@/modules/home/components/OrganizationInvite.vue')

export default {
    name: 'DrawerOrganizationSettingsSection',
    components: { OrganizationMenu, SecretCreateDialog, CreateAndEditTokenDialog, OrganizationInvite },
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        expanded: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        ...mapGetters('orgStore', ['isOrgManager']),
        ...mapState(['userOrgs', 'currentOrgData']),
        currentOrgOid() {
            return this.$route.params.oid || this.currentOrgData?.oid || this.userOrgs[0]?.oid
        }
    },
    methods: {
        openOrgInviteFromDrawer() {
            const inviteDialog = this.$refs.drawerOrganizationInvite
            if (inviteDialog && inviteDialog.openDialog) {
                inviteDialog.openDialog()
            }
        }
    }
}
</script>
